import { Link } from 'react-router-dom';
import { Pointer } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { SEO_CONFIG } from '@/lib/seo';
import { SectionHeading } from '@/components/sections/SectionHeading';
import { ContactSection } from '@/components/sections/ContactSection';
import { FadeInOnScroll } from '@/components/shared/FadeInOnScroll';
import { AnimatedHeroTree } from '@/components/shared/AnimatedHeroTree';
import { MosaicCard } from '@/components/sections/MosaicCard';
import { buttonClasses } from '@/components/ui/buttonStyles';
import { ROUTES } from '@/lib/routes';
import { PAGE_IMAGES } from '@/lib/assets';

export default function Home() {
  const diferenciaisCardHeight = 'min-h-[200px]! md:min-h-[420px]!';

  return (
    <PageLayout seo={SEO_CONFIG.home}>
      {/* HERO — 2-column: pill + headline + copy + outline CTA on left, tree image on right */}
      <section className="relative isolate bg-brand-purple text-white overflow-hidden h-auto lg:h-[985px] lg:max-h-[985px]">
        <div className="container-page grid gap-8 lg:gap-10 lg:grid-cols-2 items-center pt-28 md:pt-32 lg:py-0 h-full">
          <div className="space-y-5 md:space-y-7 max-w-xl">
            <span className="inline-block rounded-full bg-brand-yellow px-4 py-1.5 md:px-5 md:py-2 text-xs md:text-sm lg:text-base font-bold text-brand-purple">
              O melhor caminho para o seu acordo.
            </span>

            <h1 className="leading-[0.95] text-5xl md:text-6xl lg:text-8xl pt-2 lg:pt-12">
              <span className="font-bold block text-white">Pessoas</span>
              <span className="block">
                <span className="text-white">no </span>
                <span className="text-brand-yellow font-bold">Centro.</span>
              </span>
            </h1>

            <p className="text-sm md:text-base lg:text-lg text-white/90 leading-relaxed">
              <strong className="font-bold text-white">
                Nehuma inovação funciona sem o toque humano. Nossa tecnologia
                serve para potencializar as relações e facilitar a vida das
                pessoas.
              </strong>{' '}
              <span className="text-white/80">
                Facilitamos a sua jornada de negociação. Unimos esse cuidado no
                atendimento a ferramentas digitais para entender a sua
                realidade e oferecer propostas justas, rápidas e no seu tempo
              </span>
            </p>

            <div className="pt-1 md:pt-2">
              <button
                type="button"
                onClick={() =>
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="inline-flex items-center gap-2 rounded-full border border-white/90 px-5 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-medium text-white hover:bg-white hover:text-brand-purple transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-yellow cursor-pointer"
              >
                <Pointer className="h-5 w-5 shrink-0" />
                Ver Minhas Propostas
              </button>
            </div>
          </div>

          <AnimatedHeroTree
            src={PAGE_IMAGES.home.heroBg}
            alt="Árvore Araucária estilizada com elementos tecnológicos"
          />
        </div>
      </section>

      {/* Nossos Diferenciais — yellow accent rows top & bottom */}
      <div className="w-full h-[15px] md:h-[50px] bg-brand-yellow" aria-hidden="true" />
        <section className="bg-brand-lightgray py-4 md:py-28">
          <div className="container-page">
            <div className="grid gap-10 md:gap-12 lg:gap-16 md:grid-cols-2 items-center">
              <FadeInOnScroll>
                <div className="flex flex-col items-start">
                  <SectionHeading
                    top="Nossos"
                    italic="Diferenciais"
                    variant="with-highlight"
                    isItalic={false}
                    align="left"
                    size="lg"
                subtitle={
                  <span className="text-sm md:text-lg">
                    Na Arcoll Tech, acreditamos que a{' '}
                        <strong className="font-semibold text-brand-purple">
                          tecnologia só atinge seu propósito máximo quando serve para
                          fortalecer laços.
                        </strong>{' '}
                        Nossa &quot;Árvore de Inovação&quot; não é feita apenas de
                        códigos, mas sim da força de cada conexão que estabelecemos.
                        Aqui, a inovação serve para{' '}
                        <strong className="font-semibold text-brand-purple">
                          humanizar processos, facilitar o diálogo e garantir que
                          você seja sempre ouvido,
                        </strong>{' '}
                    em cada negociação.
                  </span>
                }
                  />
                  <Link
                    to={ROUTES.nossosDiferenciais}
                    className={`${buttonClasses('primary-purple', 'md')} mt-2 md:mt-8`}
                  >
                    <Pointer className="h-4 w-4" />
                    Saber mais
                  </Link>
                </div>
              </FadeInOnScroll>

              <div className="grid grid-cols-2 gap-4">
                <FadeInOnScroll>
                  <MosaicCard
                    variant="image-photo"
                    src={PAGE_IMAGES.home.diferenciais1}
                    alt="Equipe Arcoll Tech em atendimento"
                    className={diferenciaisCardHeight}
                    animate={false}
                  />
                </FadeInOnScroll>
                <FadeInOnScroll delay={0.08}>
                  <MosaicCard
                    variant="image-photo"
                    src={PAGE_IMAGES.home.diferenciais2}
                    alt="Tecnologia e dados Arcoll Tech"
                    className={diferenciaisCardHeight}
                    imgClassName="object-[60%_10%]"
                    animate={false}
                  />
                </FadeInOnScroll>
              </div>
            </div>
          </div>
        </section>
      <div className="w-full h-0 md:h-[50px] bg-brand-yellow" aria-hidden="true" />

      <ContactSection backgroundImage={PAGE_IMAGES.home.subheroBg} />
    </PageLayout>
  );
}
